/*
XOL-487 — the single rule for which absolute Rate #N positions a sharer rate
prices, shared by every surface that renders a rate's tier grid.

`RateTiersCell.vue` used to hardcode six columns (#1..#6) regardless of the
rate's [fromSharer, toSharer] band, so a rate banded e.g. 2-3 rendered #1..#6
with #4 showing a real (but unpriced) value — the XOL-487 bug report. This
helper is the one place that turns a row's band into positions; every caller
(the read-only cell, the tier editor's own copy of the same rule in
SharerRateTierCard.vue:212-226, and SharerRateVersion's responsive hint) must
agree for every VALID [fromSharer, toSharer] pair — they do. They diverge on
an out-of-range/non-numeric `toSharer`: TierCard clamps it DOWN to 1 (a
single position), this file clamps it UP to 10 (the full band) — see the
"deliberate consequences" note below for why bandPositions picks 10. For
`toSharer: null` this produces `[1]` vs `[1..10]`. That divergence is
unreachable today: `SharerRateDetails.normalizeHeader()` always forces
row.toSharer to a valid 1..10 number (defaulting to 10) before the tier
editor ever mounts, so TierCard's `ratePositions` never actually sees an
invalid `toSharer` in practice. If that guarantee ever moves (e.g. the
editor starts rendering from an un-normalized row), this pair must be
reconciled — probably by pointing TierCard at bandPositions() directly.

Rule (mirrors SharerRateTierCard.vue's `ratePositions` and
SharerRateDetails.vue's `effectiveFromSharer`):

    from = Number(row.fromSharer); if not finite or <1 or >10 -> 1
    to   = Number(row.toSharer);   if not finite or <1 or >10 -> 10
    if from > to -> from = 1        // mirrors effectiveFromSharer's transient guard
    return [from .. to]             // never empty, length 1..10

Two deliberate consequences of clamping missing/null `toSharer` to 10 rather
than branching on "is the band known":

  - Absent band (a version predating XOL-500) falls through to [1..10], with
    no `if (bandUnknown)` special case.
  - `toSharer: null` (the API's per-version contract, meaning "no upper
    bound") lands on the exact same line and resolves to 10, which IS the top
    of the position space. One rule, two meanings, no branch.

  XOL-503: the two bullets above said "today's version-history/version-snapshot
  payloads carry no fromSharer/toSharer at all" and called the per-version band
  a "documented FUTURE contract". Both went stale the moment XOL-500 shipped it
  (API c0bcf804) — the versions endpoints emit a band per version now.

  The same paragraph also claimed a widened API would render correctly "with
  zero SPA changes". That was flatly wrong, and it is the reason XOL-503
  existed: this file degraded fine, but all three consuming surfaces rebuilt a
  band-less object literal and threw the new keys away one line after they
  arrived. Degrading gracefully here never implied the callers were passing
  anything through. Kept as a correction rather than deleted, because the
  deleted version of this claim is exactly the kind that gets re-introduced.

ES5-flavoured (var, function expressions, no `??`/`?.`) — Babel 6, see the
repo's CLAUDE.md.
------------------------------------------------------------------------ */

// The rate model's absolute position ceiling (Rate #1..#10).
var RATE_POSITION_MAX = 10

// A LAYOUT constant, not a rate-model fact: how many rate columns the grid
// can show before the rest become drawer-only.
var RATE_COLS_MAX = 6

// How many of the rendered (<= RATE_COLS_MAX) positions show below the
// 1200px breakpoint.
var RATE_COLS_NARROW = 3

/**
 * @param {Object} row - a rate-bearing row, expected to carry `fromSharer`/
 *   `toSharer` (both optionally absent/null — see file header). May be
 *   null/undefined; degrades to the full [1..10] band.
 * @returns {Array<Number>} in-band absolute positions, ascending, never
 *   empty, length 1..10.
 */
function bandPositions (row) {
  var r = row || {}

  var from = Number(r.fromSharer)
  if (!isFinite(from) || from < 1 || from > RATE_POSITION_MAX) from = 1

  var to = Number(r.toSharer)
  if (!isFinite(to) || to < 1 || to > RATE_POSITION_MAX) to = RATE_POSITION_MAX

  // Committed inverted band (Min > Max) — mirrors SharerRateDetails.vue's
  // effectiveFromSharer transient guard so the read-only cell and the tier
  // editor agree while the user is mid-fix.
  if (from > to) from = 1

  var out = []
  for (var i = from; i <= to; i++) out.push(i)
  return out
}

/**
 * The first RATE_COLS_MAX in-band positions — what the grid actually renders
 * at any width. Positions beyond this are drawer-only (see hiddenRateCount).
 */
function visibleRatePositions (row) {
  return bandPositions(row).slice(0, RATE_COLS_MAX)
}

/**
 * How many in-band positions are past the RATE_COLS_MAX the grid can show.
 * 0 when the whole band fits — the "+N more in drawer" affordance is
 * suppressed at 0 by its caller.
 */
function hiddenRateCount (row) {
  return Math.max(0, bandPositions(row).length - RATE_COLS_MAX)
}

// XOL-626 (D18/S18): how many tier rows the grid can show before the rest are drawer-only.
// RateTiersCell's own constant, exported so the rate card counts what the cell hides.
var MAX_VISIBLE_TIERS = 5

/**
 * XOL-626 (S18): the rate columns to render when ONE position must stay on screen.
 *
 * `visibleRatePositions` is the first RATE_COLS_MAX (6) in-band positions; below 1200px only the
 * first RATE_COLS_NARROW (3) show. A highlighted position outside what shows is SWAPPED IN for the
 * last visible column, so it is always on screen, and the header marks the jump with a `…`.
 *
 *   wide   the columns at >=1200px (<= 6). The last one is the highlighted position when it is
 *          past the 6th in-band position (`gapWide`).
 *   narrow the columns below 1200px (<= 3), a subset of `wide`: the first two plus the
 *          highlighted position when that is not among the first three of `wide` (`gapNarrow`).
 *
 * `highlight` must be an in-band position, else nothing is swapped (a position this rate does not
 * price has no column to show). No highlight -> exactly visibleRatePositions / its first three.
 */
function displayedPositions (row, highlight) {
  var band = bandPositions(row)
  var wide = band.slice(0, RATE_COLS_MAX)
  var n = Number(highlight)
  var inBand = highlight !== null && highlight !== undefined && highlight !== '' && band.indexOf(n) !== -1
  var gapWide = false
  var gapNarrow = false

  if (inBand && wide.indexOf(n) === -1) {
    wide = wide.slice(0, RATE_COLS_MAX - 1).concat([n])
    gapWide = true
  }

  var narrow = wide.slice(0, RATE_COLS_NARROW)

  if (inBand && narrow.indexOf(n) === -1) {
    narrow = wide.slice(0, RATE_COLS_NARROW - 1).concat([n])
    gapNarrow = true
  }

  return { wide: wide, narrow: narrow, gapWide: gapWide, gapNarrow: gapNarrow, highlight: inBand ? n : null }
}

/**
 * XOL-626 (S18): the tier `seqNo`s to render, ascending. The first MAX_VISIBLE_TIERS, except that a
 * highlighted tier past them REPLACES the last one (`gap` is true then). `tiers` is the normalised
 * list (objects with `seqNo`), already sorted. The match is by `seqNo`, never by array index.
 */
function displayedTierSeqs (tiers, highlightSeq) {
  var seqs = (tiers || []).map(function (t) { return Number(t.seqNo) })
  var shown = seqs.slice(0, MAX_VISIBLE_TIERS)
  var n = Number(highlightSeq)
  var found = highlightSeq !== null && highlightSeq !== undefined && highlightSeq !== '' && seqs.indexOf(n) !== -1
  var gap = false

  if (found && shown.indexOf(n) === -1) {
    shown = shown.slice(0, MAX_VISIBLE_TIERS - 1).concat([n])
    gap = true
  }

  return { shown: shown, gap: gap, highlight: found ? n : null }
}

export {
  RATE_POSITION_MAX, RATE_COLS_MAX, RATE_COLS_NARROW, MAX_VISIBLE_TIERS,
  bandPositions, visibleRatePositions, hiddenRateCount, displayedPositions, displayedTierSeqs
}

export default {
  RATE_POSITION_MAX: RATE_POSITION_MAX,
  RATE_COLS_MAX: RATE_COLS_MAX,
  RATE_COLS_NARROW: RATE_COLS_NARROW,
  MAX_VISIBLE_TIERS: MAX_VISIBLE_TIERS,
  bandPositions: bandPositions,
  visibleRatePositions: visibleRatePositions,
  hiddenRateCount: hiddenRateCount,
  displayedPositions: displayedPositions,
  displayedTierSeqs: displayedTierSeqs
}
